import { Button, InputNumber, Modal, Popconfirm, Space, Tooltip } from "antd";
import {
  Check,
  CircleCheckBig,
  Clock,
  NotebookPen,
  Pause,
  Play,
  RefreshCw,
  Undo2,
  UserX,
  X,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ExamPeriod,
  ExamPeriodMonitor,
  ExamPeriodMonitorState,
  ExamPeriodState,
} from "@types";
import { useAddTimeMonitor, useEditApplicantMonitor, useReopenExamInstance, useSignalRConnection } from "@hooks";
import { motion } from "framer-motion";
import { getKeycloakRealm } from "../helpers";

interface Props {
  examPeriodActivity: ExamPeriodMonitor;
  examPeriod?: ExamPeriod;
}

export const ExamPeriodActivityTableActions = ({
  examPeriodActivity,
  examPeriod,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const editApplicantMonitor = useEditApplicantMonitor(examPeriod);
  const addTimeMonitor = useAddTimeMonitor(examPeriod);
  const reopenExamInstance = useReopenExamInstance(examPeriod);

  const [addTimeOpen, setAddTimeOpen] = useState(false);
  const [addTimeMinutes, setAddTimeMinutes] = useState<number>(5);

  const {
    sendPauseSingleParticipantsExamInstance,
    sendContinueSingle,
    sendDisqualifySingle,
    sendNoShowSingle,
    sendCheckoutSingle,
    sendRegisterSingle,
  } = useSignalRConnection();
  const iconDisabled =
    examPeriod?.state === ExamPeriodState.Completed ||
    examPeriodActivity.state === ExamPeriodMonitorState.Completed
      ? true
      : false;

  const displayButtonsByState = () => {
    switch (examPeriodActivity.state) {
      case ExamPeriodMonitorState.WaitingForParticipant:
        return (
          <motion.div
            key={1}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <Space>
              <Tooltip
                title={t("pages.examPeriods.registerApplicant")}
                placement="left"
              >
                <Button
                  disabled={iconDisabled}
                  loading={editApplicantMonitor.isPending}
                  onClick={() => {
                    editApplicantMonitor.mutate(
                      {
                        id: examPeriodActivity.id,
                        state: 2,
                      },
                      {
                        onSuccess: () =>
                          sendRegisterSingle(
                            examPeriodActivity.userExaminee.externalId!,
                            "register-" + examPeriodActivity.id
                          ),
                      }
                    );
                  }}
                  icon={<Check />}
                />
              </Tooltip>
              <Tooltip
                title={t("pages.examPeriods.applicantNotCome")}
                placement="right"
              >
                <Button
                  disabled={iconDisabled}
                  loading={editApplicantMonitor.isPending}
                  onClick={() => {
                    editApplicantMonitor.mutate(
                      {
                        id: examPeriodActivity.id,
                        state: 3,
                      },
                      {
                        onSuccess: () =>
                          sendNoShowSingle(
                            examPeriodActivity.userExaminee.externalId!,
                            "noShow-" + examPeriodActivity.id
                          ),
                      }
                    );
                  }}
                  icon={<X />}
                />
              </Tooltip>
            </Space>
          </motion.div>
        );
      case ExamPeriodMonitorState.ParticipantConfirmed:
        return (
          <motion.div
            key={2}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <Space>
              <Tooltip
                title={t("pages.examPeriods.checkoutApplicant")}
                placement="left"
              >
                <Button
                  disabled={iconDisabled}
                  loading={editApplicantMonitor.isPending}
                  onClick={() => {
                    editApplicantMonitor.mutate(
                      {
                        id: examPeriodActivity.id,
                        state: 1,
                      },
                      {
                        onSuccess: () =>
                          sendCheckoutSingle(
                            examPeriodActivity.userExaminee.externalId!,
                            "checkout-" + examPeriodActivity.id
                          ),
                      }
                    );
                  }}
                  icon={<CircleCheckBig style={{ color: "green" }} />}
                />
              </Tooltip>
            </Space>
          </motion.div>
        );
      case ExamPeriodMonitorState.NoShow:
        return (
          <motion.div
            key={3}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <Tooltip
              title={t("pages.examPeriods.applicantCome")}
              placement="left"
            >
              <Button
                type="dashed"
                disabled={iconDisabled}
                icon={<Undo2 />}
                loading={editApplicantMonitor.isPending}
                onClick={() =>
                  editApplicantMonitor.mutate({
                    id: examPeriodActivity.id,
                    state: 1,
                  })
                }
              />
            </Tooltip>
          </motion.div>
        );
      case ExamPeriodMonitorState.Completed:
      case ExamPeriodMonitorState.Excluded:
        return (
          <motion.div
            key={4}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <Space>
              <Tooltip title={t("pages.examPeriods.gradeExam")} placement="right">
                <Button
                  type="dashed"
                  icon={<NotebookPen />}
                  onClick={() =>
                    navigate(`/${getKeycloakRealm}/exam-periods/correction/${examPeriodActivity.id}`)
                  }
                />
              </Tooltip>
              {examPeriodActivity.state === ExamPeriodMonitorState.Completed && (
                <Tooltip title={t("pages.examPeriods.reopenExam")} placement="right">
                  <Popconfirm
                    title={t("pages.examPeriods.reopenExamConfirm")}
                    okButtonProps={{ loading: reopenExamInstance.isPending }}
                    onConfirm={() =>
                      reopenExamInstance.mutate(examPeriodActivity.id)
                    }
                  >
                    <Button type="dashed" icon={<RefreshCw />} />
                  </Popconfirm>
                </Tooltip>
              )}
            </Space>
          </motion.div>
        );
      case ExamPeriodMonitorState.InProgress:
        return (
          <motion.div
            key={5}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <Space>
              <Tooltip title={t("pages.examPeriods.pause")} placement="left">
                <Button
                  type="dashed"
                  icon={<Pause />}
                  loading={editApplicantMonitor.isPending}
                  onClick={() => {
                    editApplicantMonitor.mutate(
                      {
                        id: examPeriodActivity.id,
                        state: ExamPeriodMonitorState.Paused,
                      },
                      {
                        onSuccess: () =>
                          sendPauseSingleParticipantsExamInstance(
                            examPeriodActivity.userExaminee.externalId!,
                            "pause-" + examPeriodActivity.id
                          ),
                      }
                    );
                  }}
                />
              </Tooltip>
              <Tooltip
                title={t("pages.examPeriods.disqualified")}
                placement="right"
              >
                <Popconfirm
                  title={t("pages.examPeriods.disqualifiedConfirm")}
                  okButtonProps={{ loading: editApplicantMonitor.isPending }}
                  onConfirm={() => {
                    editApplicantMonitor.mutate(
                      {
                        id: examPeriodActivity.id,
                        state: ExamPeriodMonitorState.Excluded,
                      },
                      {
                        onSuccess: () =>
                          sendDisqualifySingle(
                            examPeriodActivity.userExaminee.externalId,
                            "disqualify-" + examPeriodActivity?.id
                          ),
                      }
                    );
                  }}
                >
                  <Button type="primary" danger icon={<UserX />} />
                </Popconfirm>
              </Tooltip>
              <Tooltip title={t("pages.examPeriods.addTime")} placement="right">
                <Button
                  type="dashed"
                  icon={<Clock />}
                  onClick={() => setAddTimeOpen(true)}
                />
              </Tooltip>
            </Space>
          </motion.div>
        );
      case ExamPeriodMonitorState.Paused:
        return (
          <motion.div
            key={6}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <Space>
              <Tooltip title={t("pages.examPeriods.continue")} placement="left">
                <Button
                  type="dashed"
                  icon={<Play />}
                  loading={editApplicantMonitor.isPending}
                  onClick={() => {
                    editApplicantMonitor.mutate(
                      {
                        id: examPeriodActivity.id,
                        state: ExamPeriodMonitorState.InProgress,
                      },
                      {
                        onSuccess: () =>
                          sendContinueSingle(
                            examPeriodActivity.userExaminee.externalId!,
                            "continue-" + examPeriodActivity.id
                          ),
                      }
                    );
                  }}
                />
              </Tooltip>
              <Tooltip title={t("pages.examPeriods.addTime")} placement="right">
                <Button
                  type="dashed"
                  icon={<Clock />}
                  onClick={() => setAddTimeOpen(true)}
                />
              </Tooltip>
            </Space>
          </motion.div>
        );
      default:
        return <></>;
    }
  };
  return (
    <div>
      {displayButtonsByState()}
      <Modal
        title={t("pages.examPeriods.addTimeTitle")}
        open={addTimeOpen}
        onOk={() => {
          addTimeMonitor.mutate(
            { id: examPeriodActivity.id, deltaSec: (addTimeMinutes ?? 0) * 60 },
            { onSettled: () => { setAddTimeOpen(false); setAddTimeMinutes(5); } }
          );
        }}
        onCancel={() => { setAddTimeOpen(false); setAddTimeMinutes(5); }}
        okText={t("pages.examPeriods.addTimeConfirm")}
        confirmLoading={addTimeMonitor.isPending}
      >
        <InputNumber
          min={-120}
          max={480}
          value={addTimeMinutes}
          onChange={(v) => setAddTimeMinutes(v ?? 0)}
          addonAfter={t("pages.examPeriods.addTimeUnit")}
          style={{ width: "100%" }}
          autoFocus
        />
      </Modal>
    </div>
  );
};
